import React from "react";
import ReactDOM from "react-dom";
import { Icon } from "@iconify/react";

export default function StaffAsyncActionModal({
  open,
  setOpen,
  title,
  phase = "loading",
  loadingTitle = "Please wait",
  loadingText = "This can take a few seconds. Stay on this screen.",
  resultTitle,
  resultText,
  resultSteps,
  actions = [],
}) {
  const busy = phase === "loading";
  const success = phase === "success";
  const warn = phase === "warn";
  const steps = Array.isArray(resultSteps)
    ? resultSteps.filter(Boolean)
    : [];

  const primaryActions = actions.filter((a) => a.primary || a.danger);
  const secondaryActions = actions.filter((a) => !a.primary && !a.danger);
  const useSplitActions =
    primaryActions.length === 1 && secondaryActions.length >= 2;

  function close() {
    if (busy) return;
    setOpen?.(false);
  }

  function renderAction(action) {
    return (
      <button
        key={action.label}
        type="button"
        onClick={action.onClick}
        disabled={action.loading}
        className={
          action.danger
            ? "h-10 flex-1 rounded-xl bg-red-600 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
            : action.primary
              ? "h-10 w-full rounded-xl bg-primary1 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
              : "h-10 min-w-[calc(50%-4px)] flex-1 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        }
      >
        {action.loading ? (
          <span className="buttonLoader mx-auto" />
        ) : (
          action.label
        )}
      </button>
    );
  }

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Close overlay"
        className="absolute inset-0 bg-black/40"
        onClick={close}
      />
      <div className="relative w-full max-w-[420px] rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 px-5 pt-4">
          <h2 className="text-[16px] font-semibold text-gray-900 leading-5">
            {title}
          </h2>
          {!busy ? (
            <button
              type="button"
              onClick={close}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <Icon icon="material-symbols:close" className="text-xl" />
            </button>
          ) : null}
        </div>

        <div className="px-5 pb-5 pt-3">
          {busy ? (
            <div className="flex flex-col items-center text-center py-5">
              <div className="mb-4 h-10 w-10 rounded-full bg-gray-50 flex items-center justify-center">
                <div className="primaryLoader" />
              </div>
              <p className="text-sm font-semibold text-gray-900">
                {loadingTitle}
              </p>
              <p className="mt-1.5 text-sm text-gray-500 max-w-[300px]">
                {loadingText}
              </p>
            </div>
          ) : (
            <div className="flex flex-col">
              <div className="flex items-start gap-3">
                <div
                  className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${
                    success
                      ? "bg-emerald-50"
                      : warn
                        ? "bg-red-50"
                        : "bg-amber-50"
                  }`}
                >
                  <Icon
                    icon={
                      success
                        ? "mdi:check-circle-outline"
                        : warn
                          ? "mdi:alert"
                          : "mdi:alert-circle-outline"
                    }
                    className={`text-xl ${
                      success
                        ? "text-emerald-600"
                        : warn
                          ? "text-red-600"
                          : "text-amber-600"
                    }`}
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-[15px] font-semibold text-gray-900 leading-5">
                    {resultTitle}
                  </h3>
                  {resultText ? (
                    <p className="mt-1 text-sm leading-5 text-gray-500">
                      {resultText}
                    </p>
                  ) : null}
                </div>
              </div>

              {steps.length ? (
                <ol className="mt-3 space-y-1.5 rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2.5 text-sm text-gray-800">
                  {steps.map((step, index) => (
                    <li key={step} className="flex gap-2 leading-5">
                      <span className="shrink-0 font-semibold text-amber-800">
                        {index + 1}.
                      </span>
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
              ) : null}

              {actions?.length ? (
                useSplitActions ? (
                  <div className="mt-4 flex flex-col gap-2">
                    {primaryActions.map(renderAction)}
                    <div className="flex flex-wrap gap-2">
                      {secondaryActions.map(renderAction)}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 flex flex-col gap-2">
                    {actions.map(renderAction)}
                  </div>
                )
              ) : null}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
